import * as React from 'react';
import { FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';
import { BarChartWrapper } from '../charts/BarChartWrapper';
import { DiagnosisReadDTO } from '../../generated/api-client';
import { dateLikeToYearCoalesce } from '../../utils/date.utils';
import { isDiagnosisClosed } from '../../utils/diagnosisStatus';

interface Props {
  diagnoses: DiagnosisReadDTO[];
}

export const HomeDiagnosisBarChartCard: React.FC<Props> = ({ diagnoses }) => {
  const [yearRangeMode, setYearRangeMode] = React.useState<'current' | 'last5'>('last5');
  const currentYear = new Date().getFullYear();
  const years = React.useMemo(
    () => [currentYear - 4, currentYear - 3, currentYear - 2, currentYear - 1, currentYear],
    [currentYear]
  );
  const visibleYears = yearRangeMode === 'current' ? [currentYear] : years;

  const yearOf = (d: DiagnosisReadDTO) =>
    dateLikeToYearCoalesce(d.diagnosis_date, d.last_update, d.data_generated_from);

  const openByYear = visibleYears.map(year =>
    diagnoses.filter(d => !isDiagnosisClosed(d) && yearOf(d) === year).length
  );
  const closedByYear = visibleYears.map(year =>
    diagnoses.filter(d => isDiagnosisClosed(d) && yearOf(d) === year).length
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Typography variant="h6">Diagnosi per anno</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="home-chart-years-label">Intervallo</InputLabel>
              <Select
                labelId="home-chart-years-label"
                value={yearRangeMode}
                label="Intervallo"
                onChange={e => setYearRangeMode(e.target.value as 'current' | 'last5')}
              >
                <MenuItem value="current">Anno in corso</MenuItem>
                <MenuItem value="last5">4 anni precedenti</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>

        <BarChartWrapper
          height={280}
          sx={{ width: '100%', maxWidth: '100%' }}
          xAxis={[{ scaleType: 'band', data: visibleYears.map(String), label: 'Anno' }]}
          series={[
            { label: 'Aperte', data: openByYear, color: '#2e7d32' },
            { label: 'Chiuse', data: closedByYear, color: '#d32f2f' },
          ]}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'middle' },
              // Spazio tra bordo SVG e righe della legenda (la legenda è disegnata nella fascia superiore del viewBox).
              padding: { top: 8, bottom: 4, left: 8, right: 8 },
            },
          }}
          // top abbastanza alto perché l’area del plot (barre) inizi sotto la legenda nello stesso canvas SVG.
          margin={{ left: 50, right: 20, top: 56, bottom: 40 }}
        />
      </Stack>
    </Paper>
  );
};

